import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight, Map, MessageCircle, ShieldCheck, Sparkles, Users, Wallet, Plus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";

export default function HomePage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const firstName =
    (profile?.name || profile?.display_name || user?.displayName || "").split(" ")[0] || "there";

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-emerald-50 pointer-events-none" />
        <div className="relative max-w-md mx-auto px-5 pt-10 pb-12">
          <div className="inline-flex items-center gap-1.5 bg-white/80 backdrop-blur border border-slate-200 rounded-full px-3 py-1 text-xs font-medium text-slate-700 mb-5 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            Group trips that actually happen
          </div>

          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05] mb-3">
            Lock your trip. <br />
            <span className="text-indigo-600">Make plans real.</span>
          </h1>
          <p className="text-base text-slate-600 leading-relaxed mb-7">
            Stop planning in WhatsApp chaos. Create a trip, invite your crew, and lock commitments with a small advance — paid directly to you via UPI.
          </p>

          <div className="flex flex-col gap-3" data-testid="home-cta-stack">
            <button
              data-testid="create-trip-btn"
              onClick={() => navigate(user ? "/create" : "/login")}
              className="w-full h-14 rounded-full bg-indigo-600 text-white font-semibold shadow-lg shadow-indigo-600/25 hover:bg-indigo-700 active:scale-95 transition-all flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {user ? `Plan a trip, ${firstName}` : "Plan a trip"}
              <ArrowRight className="w-5 h-5" />
            </button>
            {user ? (
              <button
                data-testid="my-trips-btn"
                onClick={() => navigate("/my-trips")}
                className="w-full h-12 rounded-full bg-white text-slate-900 font-medium border border-slate-200 hover:bg-slate-50 active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                <Map className="w-4 h-4" />
                My Trips
              </button>
            ) : (
              <p className="text-center text-xs text-slate-500">
                Free • Sign in with Google • No payments held by us
              </p>
            )}
          </div>
        </div>

        {/* Floating trip preview card */}
        <div className="relative max-w-md mx-auto px-5 -mt-2 pb-6">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs text-slate-500">Goa Reset 2026</div>
                <div className="font-semibold text-slate-900">15 – 18 Mar · Hosted by Rahul</div>
              </div>
              <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-semibold">Filling fast</span>
            </div>
            <div className="space-y-2">
              <ParticipantPreview name="Rahul" status="Locked in" tone="emerald" host />
              <ParticipantPreview name="Priya" status="Awaiting host" tone="amber" />
              <ParticipantPreview name="Akash" status="Unpaid" tone="slate" />
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-xs text-slate-500">Trip progress</span>
                <span className="text-xs font-semibold text-slate-700">1 / 4 locked in</span>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full w-1/4 bg-gradient-to-r from-indigo-500 to-emerald-500 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-md mx-auto px-5 mt-2">
        <h2 className="text-2xl font-bold text-slate-900 mb-5">How it works</h2>
        <div className="space-y-3">
          <Step n={1} title="Create the trip" body="Add destination, dates, cost and your UPI ID. 60 seconds." color="indigo" icon={<Plus className="w-4 h-4" />} />
          <Step n={2} title="Share with your crew" body="One-tap WhatsApp invite. Friends open and join the trip." color="indigo" icon={<MessageCircle className="w-4 h-4" />} />
          <Step n={3} title="They lock their spot" body="Each friend pays the seat-lock amount directly to your UPI." color="emerald" icon={<Wallet className="w-4 h-4" />} />
          <Step n={4} title="You confirm. Trip happens." body="Verify payments in your UPI app. Tap confirm. Bookings begin." color="emerald" icon={<ShieldCheck className="w-4 h-4" />} />
        </div>
      </section>

      {/* WHATSAPP CHAOS vs TRIPLATCH */}
      <section className="max-w-md mx-auto px-5 mt-10">
        <h2 className="text-2xl font-bold text-slate-900 mb-5">Why not just WhatsApp?</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-rose-50 border border-rose-100 rounded-2xl p-4">
            <div className="text-xs font-semibold text-rose-700 uppercase tracking-wide mb-2">WhatsApp</div>
            <ul className="space-y-1.5 text-sm text-rose-900">
              <li>200 unread "I'll come"s</li>
              <li>Last-minute drops</li>
              <li>Nobody books on time</li>
              <li>Plans never happen</li>
            </ul>
          </div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4">
            <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2">TripLatch</div>
            <ul className="space-y-1.5 text-sm text-emerald-900">
              <li>Real commitments</li>
              <li>Clear participant list</li>
              <li>UPI direct to host</li>
              <li>Trips actually happen</li>
            </ul>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-md mx-auto px-5 mt-10">
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            <h3 className="font-semibold text-slate-900">Built on trust, not custody</h3>
          </div>
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2"><span className="text-emerald-500">✓</span> TripLatch never holds your money</li>
            <li className="flex items-start gap-2"><span className="text-emerald-500">✓</span> Friends pay the host directly via UPI</li>
            <li className="flex items-start gap-2"><span className="text-emerald-500">✓</span> Hosts manually verify each payment</li>
            <li className="flex items-start gap-2"><span className="text-emerald-500">✓</span> Only join trips hosted by people you trust</li>
          </ul>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="max-w-md mx-auto px-5 mt-10">
        <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-2xl p-6 text-white shadow-lg shadow-indigo-600/20">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5" />
            <span className="text-sm font-semibold opacity-90">Used by real friend groups across India</span>
          </div>
          <p className="text-lg font-semibold leading-snug mb-4">
            Group trips usually fail before they begin. We're fixing that — one locked seat at a time.
          </p>
          <button
            data-testid="bottom-cta"
            onClick={() => navigate(user ? "/create" : "/login")}
            className="w-full h-12 rounded-full bg-white text-indigo-700 font-semibold hover:bg-indigo-50 active:scale-95 transition-all flex items-center justify-center gap-2"
          >
            <Lock className="w-4 h-4" />
            Start your trip
          </button>
        </div>
      </section>

      <p className="text-center text-xs text-slate-400 mt-8 px-6">
        TripLatch is a coordination platform — we never process or hold payments.
      </p>
    </div>
  );
}

function Step({ n, title, body, color = "indigo", icon }) {
  const colors = {
    indigo: "bg-indigo-100 text-indigo-700",
    emerald: "bg-emerald-100 text-emerald-700",
  };
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 flex items-start gap-3">
      <div className={`w-9 h-9 rounded-xl ${colors[color]} flex items-center justify-center flex-shrink-0 font-bold text-sm`}>
        {icon || n}
      </div>
      <div>
        <div className="font-semibold text-slate-900 mb-0.5">{title}</div>
        <div className="text-sm text-slate-600 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function ParticipantPreview({ name, status, tone, host }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold flex items-center justify-center">
          {name.charAt(0)}
        </div>
        <span className="text-sm font-medium text-slate-800">{name}</span>
        {host && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold">Host</span>}
      </div>
      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${tones[tone]}`}>{status}</span>
    </div>
  );
}
